// src/portal-app/nav/NavStack.js
//
// Per-tab push/pop navigation (W2 Task 2) — the navigation spine every later
// wave builds on. Three pieces:
//
//   <NavProvider activeTabId onTabChange homeTabId?>
//                               — owns the stacks, one per tab (iOS
//                                 convention): { [tabId]: [entry, ...] }.
//                                 Switching tabs and returning restores the
//                                 pushed screen, not the tab root. onTabChange
//                                 lets the nav layer drive tab selection
//                                 (hardware back to home, deep-link pushes).
//   useNav()                    — { push(routeId, params?, opts?), pop(n?),
//                                 popTo(routeId), replace(routeId, params?),
//                                 resetToRoot(), switchTab(tabId), stack,
//                                 depth, current }. INSIDE A PUSHED SCREEN
//                                 everything is bound to the tab that OWNS
//                                 the screen (I3): a late async pop() after
//                                 a save — with the user now on another tab
//                                 — closes the calling screen, never the
//                                 newly-active tab's. Depth-0 consumers
//                                 (shell, tab roots) target the ACTIVE tab.
//                                 push/replace with a route id absent from
//                                 ROUTES is a silent no-op (screen not built
//                                 yet). `current` is the resolved route meta
//                                 of the top screen (null at the root).
//   <NavStackHost tabId>        — overlay that renders the pushed screens.
//                                 contentClassName lets the shell hand down
//                                 its content-padding contract (.psh-main) so
//                                 pushed content clears the tab bar the same
//                                 way roots do. All tabs' stacks stay MOUNTED
//                                 (inactive ones visibility:hidden +
//                                 aria-hidden) so screen state survives tab
//                                 switches without replaying the push slide.
//                                 THE HOST OWNS THE HEADER (W2 Task 3): every
//                                 pushed screen automatically gets a
//                                 GlassNavBar with its title from route meta
//                                 and back wired to pop() — on iOS that
//                                 header is the ONLY back affordance, so it
//                                 is never left to screen authors. Root
//                                 (depth-0) tab surfaces are not rendered
//                                 here and get no header.
//   useNavChrome(chrome, deps)  — per-screen escape hatch: a pushed screen
//                                 may register extra header chrome
//                                 ({ subtitle, actions: [{ id, label, icon,
//                                 onPress }] }); cleared automatically when
//                                 the screen unmounts. `deps` re-registers
//                                 like useEffect deps (default []).
//
// Route meta: ROUTES[id].titleKey / .titleFallback (and the optional
// .subtitleKey / .subtitleFallback) may be strings or functions of the push
// params (e.g. an employee-detail title). The RESOLVED meta is passed to
// every screen as the `route` prop and exposed as useNav().current — screens
// must never import ROUTES to find their own title (import cycle + drift).
// `route` is therefore a reserved prop name: push params must not contain a
// `route` key.
//
// Android hardware back (@capacitor/app): pops when the active tab has
// depth > 0; at depth 0 on a non-home tab it returns to the home tab
// (Android bottom-nav convention: back goes to the start destination before
// exiting); only depth 0 ON home calls App.exitApp(). The plugin is imported
// LAZILY behind a host.type === 'capacitor' guard, so browser builds and
// jest never load the native module; the listener handle is removed on
// unmount (and on the race where unmount beats the async registration).
//
// Focus: aria-hidden on a subtree that keeps focus is ignored by Blink (the
// Android WebView), so the shell's aria-hidden-root mechanism only works if
// focus MOVES. On push the top screen (tabIndex -1, role="main") is focused;
// on pop focus returns to the element that triggered the push (captured at
// push time), falling back to the revealed screen.
//
// Motion: push slides in from the inline-end edge, pop reverses (260ms
// ease-out). Direction comes from --pnav-x set off useLanguage().isRTL, so
// Arabic mirrors. Under prefers-reduced-motion the swap is instant (no slide,
// and the leaving screen is hidden by CSS rather than animated out). Pushed
// screens are opaque over a token-only quiet gradient (the same brand wash as
// LiquidGlassBackground's base layer, minus the animated blobs) so stacked
// screens and the tab root never bleed through each other mid-transition.
// Tokens only — no raw hex.
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import host from '../../host';
import { useLanguage } from '../../context/LanguageContext';
import GlassNavBar, { NAV_BAR_HEIGHT } from '../shell/GlassNavBar';
import { ROUTES } from './routes';

export const TRANSITION_MS = 260;
// Two pushes of the SAME route inside this window are one double-tap, not two
// intents — the second is ignored (M2).
const DOUBLE_PUSH_MS = 300;

const CSS = `
.pnav-layer{position:absolute;inset:0;z-index:40;pointer-events:none;}
.pnav-tab{position:absolute;inset:0;}
/* Quiet brand wash (LiquidGlassBackground .lqg-layer base gradient, static):
   pushed screens must be opaque so push/pop slides never show the content
   underneath through the glass. The screen itself is the SLIDE UNIT and does
   not scroll — .pnav-body inside it does, so the host-owned GlassNavBar can
   sit above it as a fixed frosted overlay that content scrolls under. */
.pnav-screen{position:absolute;inset:0;overflow:hidden;box-sizing:border-box;pointer-events:auto;outline:none;
  background:linear-gradient(180deg,color-mix(in srgb,var(--c-green) 7%,var(--c-bg)) 0%,var(--c-bg) 45%,color-mix(in srgb,var(--c-green) 5%,var(--c-bg)) 100%);}
/* Scroll body. Its padding-block-start compensates for the header height +
   --safe-top (nav must never obscure content); this rule intentionally comes
   AFTER the shell's .psh-main sheet (same specificity, later wins) so it
   overrides only the top padding — the tab-bar bottom clearance and inline
   padding still come from the contentClassName contract (.psh-main). */
.pnav-body{position:absolute;inset:0;overflow-y:auto;box-sizing:border-box;
  padding-block-start:calc(${NAV_BAR_HEIGHT + 10}px + var(--safe-top, 0px));}
/* backwards (not both): once the slide ends the computed transform returns to
   none, so the screen stops being a containing block and position:fixed
   descendants (sheets, modals) can escape the scroll box (I2). */
.pnav-in{animation:pnavIn ${TRANSITION_MS}ms cubic-bezier(.16,.84,.36,1) backwards;}
.pnav-out{animation:pnavOut ${TRANSITION_MS}ms cubic-bezier(.55,.06,.68,.19) both;pointer-events:none;}
/* --pnav-x is +100% LTR / -100% RTL (set inline from isRTL) so the slide
   always enters from the inline-end edge. */
@keyframes pnavIn{from{transform:translateX(var(--pnav-x,100%));}to{transform:translateX(0);}}
@keyframes pnavOut{from{transform:translateX(0);}to{transform:translateX(var(--pnav-x,100%));}}
@media (prefers-reduced-motion: reduce){
  .pnav-in{animation:none;}
  .pnav-out{display:none;}
}
`;

const NavContext = createContext(null);
// Frozen: this single instance is handed to every depth-0 consumer — a
// mutation would corrupt it process-wide (M6).
const EMPTY_STACK = Object.freeze([]);

/* Resolve an entry against ROUTES: spreads the registry record and evaluates
   function-valued titleKey/titleFallback (and the optional subtitleKey/
   subtitleFallback) with the entry's params. This is the object screens
   receive as `route`, useNav() exposes as `current`, and the host-owned
   GlassNavBar (Task 3) renders its title/subtitle from. */
const resolveMetaField = (field, params) =>
    typeof field === 'function' ? field(params) : field;

function resolveRoute(entry) {
    const route = ROUTES[entry.routeId];
    if (!route) return null;
    return {
        ...route,
        routeId: entry.routeId,
        params: entry.params,
        titleKey: resolveMetaField(route.titleKey, entry.params),
        titleFallback: resolveMetaField(route.titleFallback, entry.params),
        subtitleKey: resolveMetaField(route.subtitleKey, entry.params),
        subtitleFallback: resolveMetaField(route.subtitleFallback, entry.params),
    };
}

export function NavProvider({ activeTabId, onTabChange, homeTabId = 'home', children }) {
    const [stacks, setStacks] = useState({});
    const keyRef = useRef(0);

    // Live mirrors so the (register-once) back listener and the stable
    // callbacks always see current values without re-subscribing.
    const activeRef = useRef(activeTabId);
    activeRef.current = activeTabId;
    const stacksRef = useRef(stacks);
    stacksRef.current = stacks;
    const onTabChangeRef = useRef(onTabChange);
    onTabChangeRef.current = onTabChange;
    const homeTabRef = useRef(homeTabId);
    homeTabRef.current = homeTabId;

    const makeEntry = (routeId, params) => {
        keyRef.current += 1;
        return {
            routeId,
            params: params || {},
            key: `pn${keyRef.current}`,
            at: Date.now(),
            // Captured so pop can hand focus back to the element that pushed
            // (usually the hub card / quick-action button).
            returnFocus:
                typeof document !== 'undefined' &&
                document.activeElement instanceof HTMLElement
                    ? document.activeElement
                    : null,
        };
    };

    const switchTab = useCallback((tabId) => {
        const cb = onTabChangeRef.current;
        if (cb) cb(tabId);
    }, []);

    // Every mutator takes the tab it operates on EXPLICITLY (I3, W2 final
    // review): useNav() binds pushed screens to the tab that OWNS them, so a
    // late async `pop()` (submit → await save → pop, with the user now on
    // another tab) can never close a screen on the wrong tab. `originTab`
    // null/undefined → the active tab (shell / depth-0 behaviour).
    const pushFrom = useCallback(
        (originTab, routeId, params, opts) => {
            if (!ROUTES[routeId]) return; // not built yet — silent no-op
            const tab = (opts && opts.tab) || originTab || activeRef.current;
            // Double-tap guard: same route landing twice within the window
            // is one intent (M2).
            const stack = stacksRef.current[tab] || EMPTY_STACK;
            const top = stack[stack.length - 1];
            if (top && top.routeId === routeId && Date.now() - top.at < DOUBLE_PUSH_MS) return;
            const entry = makeEntry(routeId, params);
            setStacks((s) => ({ ...s, [tab]: [...(s[tab] || []), entry] }));
            // Deep-link pushes onto another tab also select that tab, so the
            // tab bar highlight follows the content (A1).
            if (opts && opts.tab && opts.tab !== activeRef.current) switchTab(opts.tab);
        },
        [switchTab]
    );

    // popFrom(tab, n) — pop `n` levels (default 1); clamped, tolerant of a
    // stray event object being passed via onClick.
    const popFrom = useCallback((originTab, n) => {
        const count = Math.max(1, Math.floor(Number(n)) || 1);
        const tab = originTab || activeRef.current;
        setStacks((s) => {
            const stack = s[tab];
            if (!stack || !stack.length) return s;
            return { ...s, [tab]: stack.slice(0, Math.max(0, stack.length - count)) };
        });
    }, []);

    // popToFrom(tab, routeId) — pop until that screen is on top (W4
    // list→detail→edit flows). Not in the stack → no-op.
    const popToFrom = useCallback((originTab, routeId) => {
        const tab = originTab || activeRef.current;
        setStacks((s) => {
            const stack = s[tab] || EMPTY_STACK;
            const idx = stack.map((e) => e.routeId).lastIndexOf(routeId);
            if (idx < 0 || idx === stack.length - 1) return s;
            return { ...s, [tab]: stack.slice(0, idx + 1) };
        });
    }, []);

    // Swap the top entry in place (push semantics when the stack is empty).
    // No exit slide — the new screen takes the spot with an entry animation.
    const replaceFrom = useCallback((originTab, routeId, params) => {
        if (!ROUTES[routeId]) return;
        const tab = originTab || activeRef.current;
        const entry = makeEntry(routeId, params);
        setStacks((s) => {
            const stack = s[tab] || [];
            return { ...s, [tab]: [...stack.slice(0, Math.max(0, stack.length - 1)), entry] };
        });
    }, []);

    const resetFrom = useCallback((originTab) => {
        const tab = originTab || activeRef.current;
        setStacks((s) => {
            if (!s[tab] || !s[tab].length) return s;
            return { ...s, [tab]: [] };
        });
    }, []);

    // Android hardware back — pop when pushed; at depth 0 return to the home
    // tab first (Android start-destination convention); exit only from home
    // root. Lazy guarded import: web builds and jest never load the plugin.
    useEffect(() => {
        if (host.type !== 'capacitor') return undefined;
        let disposed = false;
        let handle = null;
        (async () => {
            try {
                const { App } = await import('@capacitor/app');
                const h = await App.addListener('backButton', () => {
                    const depth = (stacksRef.current[activeRef.current] || []).length;
                    if (depth > 0) popFrom(null); // hardware back → ACTIVE tab
                    else if (activeRef.current !== homeTabRef.current) {
                        switchTab(homeTabRef.current);
                    } else App.exitApp();
                });
                if (disposed) h.remove(); // unmount won the race — drop it now
                else handle = h;
            } catch {
                // Plugin unavailable — hardware back keeps the OS default.
            }
        })();
        return () => {
            disposed = true;
            if (handle) handle.remove();
        };
    }, [popFrom, switchTab]);

    const value = useMemo(
        () => ({
            stacks,
            activeTabId,
            pushFrom,
            popFrom,
            popToFrom,
            replaceFrom,
            resetFrom,
            switchTab,
        }),
        [stacks, activeTabId, pushFrom, popFrom, popToFrom, replaceFrom, resetFrom, switchTab]
    );

    return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}

/* The tab a pushed screen BELONGS to. Provided by TabStackLayer, so any
   useNav() call inside a pushed screen is bound to that tab regardless of
   which tab is active when a (possibly late, async) mutator finally fires.
   Depth-0 consumers (the shell, tab roots) have no binding and keep the
   active-tab behaviour. */
const TabBindingContext = createContext(null);

export function useNav() {
    const ctx = useContext(NavContext);
    const boundTab = useContext(TabBindingContext);
    if (!ctx) throw new Error('useNav must be used inside <NavProvider>');
    // Pushed screens: their OWN tab (I3 — a late pop() after a tab switch
    // must close the calling screen, never the newly-active tab's).
    // Everything else: the active tab.
    const tabId = boundTab || ctx.activeTabId;
    const stack = ctx.stacks[tabId] || EMPTY_STACK;
    const top = stack.length ? stack[stack.length - 1] : null;
    return useMemo(() => ({
        push: (routeId, params, opts) => ctx.pushFrom(boundTab, routeId, params, opts),
        pop: (n) => ctx.popFrom(boundTab, n),
        popTo: (routeId) => ctx.popToFrom(boundTab, routeId),
        replace: (routeId, params) => ctx.replaceFrom(boundTab, routeId, params),
        resetToRoot: () => ctx.resetFrom(boundTab),
        switchTab: ctx.switchTab,
        stack,
        depth: stack.length,
        current: top ? resolveRoute(top) : null,
    }), [ctx, boundTab, stack, top]);
}

/* ------------------------------------------------------------------ */
/* Header ownership (W2 Task 3).                                       */
/* ScreenChrome wraps EVERY pushed screen: scroll body + host-owned    */
/* GlassNavBar (title/subtitle from resolved route meta, back → pop).  */
/* Screens render no header themselves — deleting this wiring strands  */
/* iOS users with no back affordance, and fails GlassNavBar.test.js.   */
/* ------------------------------------------------------------------ */
const ScreenChromeContext = createContext(null);

// Escape hatch for screens needing extra header chrome:
//   useNavChrome({ subtitle, actions: [{ id, label, icon, onPress }] }, deps)
// Registered on mount (or when `deps` change, useEffect-style) and cleared
// when the screen unmounts. No-op outside a pushed screen. The zero-config
// path (no call at all) is already correct — title + back need nothing.
export function useNavChrome(chrome, deps = []) {
    const ctx = useContext(ScreenChromeContext);
    // Ref so the effect applies the LATEST chrome object without forcing
    // callers to memoize it — `deps` alone decide when to re-register.
    const chromeRef = useRef(chrome);
    chromeRef.current = chrome;
    useEffect(() => {
        if (!ctx) return undefined;
        ctx.set(chromeRef.current);
        return () => ctx.set(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctx, ...deps]);
}

function ScreenChrome({ route, onBack, contentClassName, children }) {
    const { t } = useLanguage();
    const [chrome, setChrome] = useState(null);
    const chromeApi = useMemo(() => ({ set: setChrome }), []);

    const title = route.titleKey
        ? t(route.titleKey, {}, route.titleFallback)
        : route.titleFallback || '';
    const routeSubtitle = route.subtitleKey
        ? t(route.subtitleKey, {}, route.subtitleFallback)
        : route.subtitleFallback || null;
    // Per-screen chrome (dynamic) wins over per-route meta (static).
    const subtitle = (chrome && chrome.subtitle) || routeSubtitle || undefined;

    return (
        <ScreenChromeContext.Provider value={chromeApi}>
            {/* Header FIRST in DOM: the a11y tree and Tab order follow DOM
               order, not z-index — Back (the only back affordance on iOS)
               and the title h1 must be reachable before the body content.
               Paint order is unaffected: .gnav carries z-index:2 while the
               body stays at z auto, so the bar still frosts above it. */}
            <GlassNavBar
                title={title}
                subtitle={subtitle}
                onBack={onBack}
                actions={(chrome && chrome.actions) || undefined}
            />
            <div className={`pnav-body ${contentClassName}`.trim()}>{children}</div>
        </ScreenChromeContext.Provider>
    );
}

/* ------------------------------------------------------------------ */
/* One tab's screens. Keeps just-popped screens mounted for the exit   */
/* slide (pnav-out) — a LIST, so a multi-level pop or a rapid second   */
/* pop never cancels an exit mid-flight (M1) — then drops each after   */
/* TRANSITION_MS. replace() keeps the length equal, so it swaps with   */
/* no exit animation. Also owns push/pop focus management (I1).        */
/* ------------------------------------------------------------------ */
function TabStackLayer({ tab, stack, active, contentClassName, onBack }) {
    const [leaving, setLeaving] = useState([]);
    // EMPTY_STACK (not `stack`) so a layer that mounts WITH content — the
    // first push creates it — still detects growth and runs push focus.
    const prevRef = useRef(EMPTY_STACK);
    const timersRef = useRef(new Set());
    const layerRef = useRef(null);

    useEffect(() => {
        const prev = prevRef.current;
        prevRef.current = stack;
        if (prev.length === stack.length) return;

        const focusTop = () => {
            const el =
                layerRef.current &&
                layerRef.current.querySelector('[data-pnav-top="true"]');
            if (el) el.focus({ preventScroll: true });
        };

        if (prev.length < stack.length) {
            // Push: move focus into the new screen so the covered content's
            // aria-hidden actually takes effect (Blink ignores aria-hidden on
            // a subtree that retains focus) and SRs announce the screen.
            if (active) focusTop();
            return;
        }

        // Pop / reset: keep the old top mounted for its exit slide…
        const prevTop = prev[prev.length - 1];
        setLeaving((l) => [...l, prevTop]);
        const timer = setTimeout(() => {
            timersRef.current.delete(timer);
            setLeaving((l) => l.filter((e) => e.key !== prevTop.key));
        }, TRANSITION_MS);
        timersRef.current.add(timer);
        // …and hand focus back to whatever pushed it (fallback: the screen
        // this pop revealed).
        if (active) {
            const rf = prevTop.returnFocus;
            if (rf && rf.isConnected && typeof rf.focus === 'function') {
                rf.focus({ preventScroll: true });
            } else focusTop();
        }
    }, [stack, active]);

    // Unmount cleanup only — an exit timer must survive later stack changes.
    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            timers.forEach(clearTimeout);
        };
    }, []);

    if (!stack.length && !leaving.length) return null;

    const renderScreen = (entry, extra) => {
        const route = resolveRoute(entry);
        if (!route) return null; // registry changed under us — skip
        const Screen = route.component;
        return (
            <div key={entry.key} {...extra}>
                <ScreenChrome
                    route={route}
                    onBack={onBack}
                    contentClassName={contentClassName}
                >
                    <Screen {...entry.params} route={route} />
                </ScreenChrome>
            </div>
        );
    };

    return (
        <div
            ref={layerRef}
            className="pnav-tab"
            aria-hidden={active ? undefined : true}
            style={active ? undefined : { visibility: 'hidden' }}
        >
            {/* Screens in this layer get a useNav() bound to THIS tab (I3):
                the chrome knows which tab it renders in, so mutators fired
                from these screens — even late, after a tab switch — always
                target the tab that owns them. */}
            <TabBindingContext.Provider value={tab}>
            {stack.map((entry, i) => {
                const top = i === stack.length - 1;
                return renderScreen(entry, {
                    className: 'pnav-screen pnav-in',
                    // The top screen is the page's landmark while the root
                    // <main> is covered + aria-hidden (M8), and the
                    // programmatic focus target on push (I1).
                    role: top ? 'main' : undefined,
                    tabIndex: -1,
                    'data-pnav-top': top ? 'true' : undefined,
                    'aria-hidden': top ? undefined : true,
                });
            })}
            {/* Exit slides paint over what they reveal: a newer departure came
                from LOWER in the stack than an older one still mid-flight, so
                render newest → oldest reversed (oldest paints on top). */}
            {[...leaving].reverse().map((entry) =>
                renderScreen(entry, {
                    className: 'pnav-screen pnav-out',
                    'aria-hidden': true,
                })
            )}
            </TabBindingContext.Provider>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* NavStackHost — absolute overlay above the tab's root content.       */
/* Renders EVERY tab's stack (inactive ones hidden, state preserved);  */
/* `tabId` picks the visible one. `contentClassName` is the shell's    */
/* content-padding contract class (e.g. "psh-main") so pushed screens  */
/* scroll and clear the tab bar exactly like root content.             */
/* The layer itself is pointer-events:none, so an empty stack never    */
/* blocks taps on the root underneath.                                 */
/* ------------------------------------------------------------------ */
export function NavStackHost({ tabId, contentClassName = '' }) {
    const ctx = useContext(NavContext);
    const { isRTL } = useLanguage();
    if (!ctx) throw new Error('NavStackHost must be used inside <NavProvider>');

    return (
        <div
            className="pnav-layer"
            data-testid="portal-nav-layer"
            style={{ '--pnav-x': isRTL ? '-100%' : '100%' }}
        >
            <style>{CSS}</style>
            {Object.entries(ctx.stacks).map(([tab, stack]) => (
                <TabStackLayer
                    key={tab}
                    tab={tab}
                    stack={stack}
                    active={tab === tabId}
                    contentClassName={contentClassName}
                    // Header ownership: every pushed screen's back button
                    // pops ITS OWN tab's stack (I3) — never the active one.
                    onBack={() => ctx.popFrom(tab)}
                />
            ))}
        </div>
    );
}
